/**
 * Variant dropdown for PDP - when product has more than 3 variants.
 * Card layout: image left, variant + price right, arrow rotates 180° when open.
 */
class VariantDropdown {
    constructor(container) {
        this.container = container;
        this.trigger = container.querySelector('.js-variant-dropdown-trigger');
        this.list = container.querySelector('.js-variant-dropdown-list');
        this.arrow = container.querySelector('.js-variant-dropdown-arrow');
        this.amountEl = container.querySelector('.js-variant-dropdown-amount');
        this.priceEl = container.querySelector('.js-variant-dropdown-price');
        this.options = container.querySelectorAll('.js-variant-dropdown-option');
        this.isOpen = false;

        this.init();
    }

    init() {
        if (!this.trigger || !this.list) return;

        this.trigger.addEventListener('click', (e) => this.toggle(e));
        this.trigger.addEventListener('keydown', (e) => this.handleTriggerKeydown(e));

        this.options.forEach((option) => {
            option.addEventListener('click', (e) => this.selectOption(e, option));
            option.addEventListener('keydown', (e) => this.handleOptionKeydown(e, option));
        });

        document.addEventListener('click', (e) => this.handleOutsideClick(e));

        // Listen for variant changes (e.g. from URL on load)
        const variantRadios = this.container.closest('variant-radios');
        if (variantRadios) {
            variantRadios.addEventListener('change', () => this.updateTriggerFromSelection());
        }

        this.updateTriggerFromSelection();
    }

    toggle(e) {
        e.preventDefault();
        e.stopPropagation();
        this.isOpen = !this.isOpen;
        this.updateOpenState();
    }

    updateOpenState() {
        if (this.isOpen) {
            this.list.hidden = false;
            this.trigger.setAttribute('aria-expanded', 'true');
            this.arrow?.classList.add('variant-dropdown-pdp__arrow--open');
        } else {
            this.list.hidden = true;
            this.trigger.setAttribute('aria-expanded', 'false');
            this.arrow?.classList.remove('variant-dropdown-pdp__arrow--open');
        }
    }

    close() {
        this.isOpen = false;
        this.updateOpenState();
    }

    selectOption(e, option) {
        if (option.classList.contains('variant-dropdown-pdp__option--disabled')) return;
        e.preventDefault();
        e.stopPropagation();

        const radio = option.querySelector('.variant-dropdown-pdp__input');
        if (radio && !radio.checked) {
            // Remove --current from previously selected option
            this.options.forEach((opt) => opt.classList.remove('variant-dropdown-pdp__option--current'));
            // Add --current to newly selected option (hide it from list)
            option.classList.add('variant-dropdown-pdp__option--current');
            radio.checked = true;
            radio.dispatchEvent(new Event('change', { bubbles: true }));
        }

        this.updateTriggerFromOption(option);
        this.close();
    }

    updateTriggerFromOption(option) {
        const amount = option.dataset.optionValue;
        const hasSale = option.dataset.hasSale === 'true';
        const price = option.dataset.price;
        const compareAtPrice = option.dataset.compareAtPrice;
        const imageUrl = option.dataset.imageUrl;

        if (this.amountEl) this.amountEl.textContent = amount;

        if (this.priceEl) {
            if (hasSale && compareAtPrice) {
                this.priceEl.innerHTML = `<s class="variant-dropdown-pdp__compare-price">${compareAtPrice}</s> <span class="variant-dropdown-pdp__sale-price text-validationError">${price}</span>`;
            } else {
                this.priceEl.textContent = price;
            }
        }

        const triggerImage = this.trigger.querySelector('.variant-dropdown-pdp__image-wrapper img');
        if (triggerImage && imageUrl) {
            triggerImage.src = imageUrl;
        }
    }

    updateTriggerFromSelection() {
        const checkedRadio = this.container.querySelector('.variant-dropdown-pdp__input:checked');
        if (!checkedRadio) return;

        const option = checkedRadio.closest('.js-variant-dropdown-option');
        if (option) {
            this.updateTriggerFromOption(option);
        }
    }

    handleTriggerKeydown(e) {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.toggle(e);
        }
        if (e.key === 'Escape') {
            this.close();
        }
    }

    handleOptionKeydown(e, option) {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.selectOption(e, option);
        }
        if (e.key === 'Escape') {
            this.close();
        }
    }

    handleOutsideClick(e) {
        if (this.isOpen && !this.container.contains(e.target)) {
            this.close();
        }
    }
}

export default VariantDropdown;
